import { basicSort, COMMON_TABLE_COLUMN_WIDTHS, geneLinkSort, numericSort, TableCellInteger, TableCellLink, TableCellNumeric, TableCellPValue, viewVariantsSort, } from 'Shared/components/Table';
import { ELevelResult, ETraitsTypes } from 'Shared/types';
import createTableQVLink from 'Common/components/Table/factories/createTableQVLink/createTableQVLink';
import createTableGeneLink from 'Common/components/Table/factories/createTableGeneLink/createTableGeneLink';
import styles from 'Shared/components/Table/components/TableView/TableView.css';
export var ETableColumnAccessor;
(function (ETableColumnAccessor) {
    ETableColumnAccessor["Variants"] = "variants";
    ETableColumnAccessor["GeneName"] = "gene.name";
    ETableColumnAccessor["Ancestry"] = "ancestry";
    ETableColumnAccessor["CollapsingModelName"] = "collapsingModel.name";
    ETableColumnAccessor["PValue"] = "pvalue";
    ETableColumnAccessor["YesQV"] = "yesQV";
    ETableColumnAccessor["NoQV"] = "noQV";
    ETableColumnAccessor["CaseMedian"] = "caseMedian";
    ETableColumnAccessor["ControlMedian"] = "controlMedian";
    ETableColumnAccessor["Beta"] = "beta";
    ETableColumnAccessor["BetaSTD"] = "betaSTD";
    ETableColumnAccessor["LCI"] = "LCI";
    ETableColumnAccessor["UCI"] = "UCI";
})(ETableColumnAccessor || (ETableColumnAccessor = {}));
const TABLE_COLUMNS_HEADERS = {
    [ETableColumnAccessor.Variants]: 'Link to variants',
    [ETableColumnAccessor.GeneName]: 'Gene',
    [ETableColumnAccessor.Ancestry]: 'Ancestry',
    [ETableColumnAccessor.CollapsingModelName]: 'Collapsing model',
    [ETableColumnAccessor.PValue]: 'P value',
    [ETableColumnAccessor.YesQV]: 'No. with QV',
    [ETableColumnAccessor.NoQV]: 'No. without QV',
    [ETableColumnAccessor.CaseMedian]: 'QV carrier median',
    [ETableColumnAccessor.ControlMedian]: 'QV non-carrier median',
    [ETableColumnAccessor.Beta]: 'Effect size',
    [ETableColumnAccessor.BetaSTD]: 'Effect size standard error',
    [ETableColumnAccessor.LCI]: 'Effect size LCI',
    [ETableColumnAccessor.UCI]: 'Effect size UCI',
};
/**
 * Get table columns
 *
 * @param descriptions - Text descriptions for the columns
 * @param referrerData - Referrer data for the variants link
 * @param columnsData - Associations data for the variants link
 * @param hasAncestryData - Boolean value which determines if ancestry data exists
 * @returns formatted table columns
 */
export const getTableColumns = (descriptions, referrerData, columnsData, hasAncestryData) => [
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Variants],
        accessor: ETableColumnAccessor.Variants,
        className: styles.alignLeft,
        Cell: TableCellLink({
            additionalData: columnsData,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableQVLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANTS_LINK,
        exported: false,
        sortType: (rowA, rowB) => viewVariantsSort(rowA, rowB, Boolean(FEATURE_FLAGS.PUBLIC_VERSION)),
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.GeneName],
        accessor: ETableColumnAccessor.GeneName,
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Gene,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableGeneLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    ...(hasAncestryData ? [
        {
            Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Ancestry],
            accessor: ETableColumnAccessor.Ancestry,
            description: descriptions[ETableColumnAccessor.Ancestry],
            fixed: true,
            width: COMMON_TABLE_COLUMN_WIDTHS.ANCESTRY,
            sortType: basicSort,
        },
    ] : []),
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CollapsingModelName],
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.PValue],
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.YesQV],
        accessor: ETableColumnAccessor.YesQV,
        description: descriptions[ETableColumnAccessor.YesQV],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.NoQV],
        accessor: ETableColumnAccessor.NoQV,
        description: descriptions[ETableColumnAccessor.NoQV],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_WITHOUT_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CaseMedian],
        accessor: ETableColumnAccessor.CaseMedian,
        description: descriptions[ETableColumnAccessor.CaseMedian],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.QV_CARRIER_MEDIAN,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ControlMedian],
        accessor: ETableColumnAccessor.ControlMedian,
        description: descriptions[ETableColumnAccessor.ControlMedian],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.QV_NON_CARRIER_MEDIAN,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Beta],
        accessor: ETableColumnAccessor.Beta,
        description: descriptions[ETableColumnAccessor.Beta],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.BetaSTD],
        accessor: ETableColumnAccessor.BetaSTD,
        description: descriptions[ETableColumnAccessor.BetaSTD],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_STANDARD_ERROR,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.LCI],
        accessor: ETableColumnAccessor.LCI,
        description: descriptions[ETableColumnAccessor.LCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_LCI,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.UCI],
        accessor: ETableColumnAccessor.UCI,
        description: descriptions[ETableColumnAccessor.UCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_UCI,
    },
];
export const getWesWgsTableColumns = (descriptions, referrerData) => [
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.GeneName],
        accessor: ETableColumnAccessor.GeneName,
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Gene,
            traitsType: ETraitsTypes.Continuous,
            referrerData,
        }, createTableGeneLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CollapsingModelName],
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.PValue],
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.YesQV],
        accessor: ETableColumnAccessor.YesQV,
        description: descriptions[ETableColumnAccessor.YesQV],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.NoQV],
        accessor: ETableColumnAccessor.NoQV,
        description: descriptions[ETableColumnAccessor.NoQV],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_WITHOUT_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Beta],
        accessor: ETableColumnAccessor.Beta,
        description: descriptions[ETableColumnAccessor.Beta],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.BetaSTD],
        accessor: ETableColumnAccessor.BetaSTD,
        description: descriptions[ETableColumnAccessor.BetaSTD],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_STANDARD_ERROR,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.LCI],
        accessor: ETableColumnAccessor.LCI,
        description: descriptions[ETableColumnAccessor.LCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_LCI,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.UCI],
        accessor: ETableColumnAccessor.UCI,
        description: descriptions[ETableColumnAccessor.UCI],
        Cell: TableCellNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.EFFECT_SIZE_UCI,
    },
];
